import {
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Box, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useLocalStorage } from "utils/useLocalStorage";
import { useWidthObserver } from "./useWidthObserver";
import HorizontalResizer from "./HorizontalResizer";

/** Smallest width, in px, a panel can be resized to. */
const MIN_PANEL_WIDTH = 200;

export interface DraggablePanel {
  /** Stable identity; also the localStorage sub-key for the panel's width. */
  key: string;
  show: boolean;
  /** Width applied until the user resizes the panel. Ignored when `grow`. */
  defaultWidth?: number;
  /** The trailing panel: fills the remaining space and carries no resizer. */
  grow?: boolean;
  content: ReactNode;
}

type PanelWidths = Record<string, number | null>;

interface DraggablePanelsProps {
  panels: DraggablePanel[];
  /** Stable suffix for the localStorage key under `datasetPanelWidths.`. */
  storageId: string;
}

/**
 * Lays panels out in a horizontal row with a draggable divider on the right
 * edge of every panel except the trailing `grow` one. Panel widths persist to
 * localStorage. Resize-only port of apps/web's DraggablePanels — the original's
 * drag-to-reorder feature is intentionally left out.
 */
export default function DraggablePanels({
  panels,
  storageId,
}: DraggablePanelsProps) {
  const panelKeys = panels.map((panel) => panel.key);

  const [panelWidths, setPanelWidths] = useState<PanelWidths>(() =>
    Object.fromEntries(panelKeys.map((key) => [key, null])),
  );
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scroll, setScroll] = useState({ left: false, right: false });

  const panelsRef = useRef<HTMLDivElement | null>(null);

  useLocalStorage<PanelWidths>({
    key: `datasetPanelWidths.${storageId}`,
    data: panelWidths,
    onLoad: (stored) => setPanelWidths((prev) => ({ ...prev, ...stored })),
  });

  useWidthObserver({ ref: panelsRef, onWidthChange: setContainerWidth });

  // Whether the row overflows far enough to scroll in either direction.
  const checkScroll = useCallback(() => {
    const el = panelsRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setScroll({
      left: el.scrollLeft > 1,
      right: el.scrollLeft < maxScroll - 1,
    });
  }, []);

  useEffect(() => {
    const el = panelsRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener("scroll", checkScroll);
    return () => el.removeEventListener("scroll", checkScroll);
  }, [checkScroll]);

  // Re-evaluate after a resize, a panel show/hide, or a container resize, none
  // of which fire a scroll event.
  useEffect(checkScroll, [checkScroll, containerWidth, panelWidths, panels]);

  const scrollToEdge = (side: "left" | "right") => {
    const el = panelsRef.current;
    if (!el) return;
    el.scrollTo({
      left: side === "left" ? 0 : el.scrollWidth,
      behavior: "smooth",
    });
  };

  const handleResize = useCallback((key: string, width: number) => {
    setPanelWidths((prev) => ({
      ...prev,
      [key]: Math.max(MIN_PANEL_WIDTH, Math.round(width)),
    }));
  }, []);

  const visiblePanels = useMemo(
    () => panels.filter((panel) => panel.show && isValidElement(panel.content)),
    [panels],
  );

  return (
    <Box sx={{ position: "relative", display: "flex", flex: 1, minWidth: 0 }}>
      {scroll.left && (
        <ScrollIndicator side="left" onClick={() => scrollToEdge("left")} />
      )}
      <Box
        ref={panelsRef}
        sx={{
          display: "flex",
          flex: 1,
          minWidth: 0,
          overflowX: "auto",
          // While dragging a divider the pointer roams over panel content;
          // suppress text selection and keep the resize cursor everywhere.
          ...(resizingKey && { userSelect: "none", cursor: "col-resize" }),
        }}
      >
        {visiblePanels.map((panel, index) => {
          const isLast = index === visiblePanels.length - 1;
          const hasResizer = !isLast && !panel.grow;
          const width = panelWidths[panel.key];

          return (
            <Box
              key={panel.key}
              data-testid={`draggablePanels.panel.${panel.key}`}
              sx={{
                position: "relative",
                display: "flex",
                minWidth: MIN_PANEL_WIDTH,
                ...(panel.grow
                  ? { flex: 1 }
                  : {
                      flexShrink: 0,
                      width: width ?? panel.defaultWidth ?? MIN_PANEL_WIDTH,
                    }),
              }}
            >
              {panel.content}
              {hasResizer && (
                <HorizontalResizer
                  resizerKey={panel.key}
                  resizingKey={resizingKey}
                  setResizingKey={setResizingKey}
                  onResize={(next) => handleResize(panel.key, next)}
                />
              )}
            </Box>
          );
        })}
      </Box>
      {scroll.right && (
        <ScrollIndicator side="right" onClick={() => scrollToEdge("right")} />
      )}
    </Box>
  );
}

function ScrollIndicator({
  side,
  onClick,
}: {
  side: "left" | "right";
  onClick: () => void;
}) {
  return (
    <IconButton
      onClick={onClick}
      size="small"
      data-testid={`draggablePanels.scroll.${side}`}
      sx={{
        position: "absolute",
        top: "50%",
        [side]: 4,
        transform: "translateY(-50%)",
        zIndex: 700,
        color: "common.white",
        bgcolor: "rgba(0, 0, 0, 0.6)",
        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.85)" },
      }}
    >
      {side === "left" ? (
        <ArrowBackIosNewIcon fontSize="small" />
      ) : (
        <ArrowForwardIosIcon fontSize="small" />
      )}
    </IconButton>
  );
}
